import React, { useEffect, useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { registrarVentaCarrito, argsVentaCarrito } from '../services/ventasService';
import { nuevoTicket, esErrorDeRed, guardarVentaPendiente } from '../services/ventasPendientes';
import { registrarComisionBancaria } from '../services/gastosService';
import { calcularComision, COMISION_POS_PCT } from '../utils/comisiones';
import { obtenerComisionPct } from '../services/negociosService';
import { obtenerClientes, crearCliente } from '../services/clientesService';
import { obtenerCuentas, iconoTipo } from '../services/cuentasService';
import CampoNumero from './CampoNumero';
import IngresoLibreModal from './IngresoLibreModal';
import ProductoRapidoModal from './ProductoRapidoModal';
import UbicacionCliente from './UbicacionCliente';
import { obtenerFotoPrincipal } from '../utils/categorias';
import { optimizarFoto } from '../utils/imagen';
import { enOferta, precioEfectivo, pctOferta } from '../utils/precios';
import { CANALES, CANAL_DEFECTO } from '../utils/canales';
import { fechaLocal, horaLocal, timestampParaFecha } from '../utils/fecha';
import { mensajeAmigable } from '../utils/errores';
import { numeroSeguro } from '../utils/validacion';
import ComprobanteModal from './ComprobanteModal';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { esPorPeso, formatoCantidad, redondearCantidad, unidadDe } from '../utils/unidades';
import { registrarResultadoPedido, obtenerApartados } from '../services/pedidosService';
import { obtenerCampanaActiva, obtenerEnvios } from '../services/recompraService';
import { canalDeOrigen, vinoPorAnuncio, itemsDePedido, clienteDePedido } from '../utils/pedidoAVenta';
import { etiquetaOrigen } from '../utils/origenCatalogo';

const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Punto de venta: arma un carrito con varios productos y cobra todo junto.
// `pedido` (opcional): venta de un pedido del catálogo. Llega con las prendas,
// la clienta, el canal y "vino por anuncio" ya puestos; al cobrar, el pedido
// queda como "Compró" (así no se registra dos veces ni se olvida marcarlo).
export default function POSView({ productos, onVolver, onVendido, onProductoCreado, pedido = null, onPedidoVendido }) {
  const { mostrarToast } = useToast();
  const { negocio, sesion } = useAuth();
  const [busqueda, setBusqueda] = useState('');
  const [ultimaVenta, setUltimaVenta] = useState(null); // { total, ticket, sinInternet } de la última venta
  const [verComprobante, setVerComprobante] = useState(false);
  const [carrito, setCarrito] = useState([]); // [{producto_id, codigo, nombre, precio, stock, foto, cantidad}]
  const [clientes, setClientes] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [cuentaId, setCuentaId] = useState('');
  const [descuento, setDescuento] = useState('');
  const [modoDesc, setModoDesc] = useState('L'); // 'L' = lempiras · '%' = porcentaje
  const [envio, setEnvio] = useState(''); // costo de envío que cobra a la clienta (dinero de paso)
  const [cobrando, setCobrando] = useState(false);
  const [mostrarIngresoLibre, setMostrarIngresoLibre] = useState(false);
  const [mostrarNuevoProd, setMostrarNuevoProd] = useState(false);
  const [categoriaFiltro, setCategoriaFiltro] = useState(''); // '' = todas
  const [pagaCon, setPagaCon] = useState(''); // efectivo: para calcular el vuelto
  const [concepto, setConcepto] = useState('');
  const [nota, setNota] = useState('');
  const [canal, setCanal] = useState(CANAL_DEFECTO);
  const [porAnuncio, setPorAnuncio] = useState(false); // la clienta llegó por un anuncio de Meta
  const [fechaVenta, setFechaVenta] = useState(fechaLocal()); // fecha de la venta (editable)
  const [aCredito, setAcredito] = useState(false); // venta a crédito (fiado)
  const [conComisionPOS, setConComisionPOS] = useState(false); // cobrado con POS/Link Ficohsa (−4%)
  const [comisionPct, setComisionPct] = useState(COMISION_POS_PCT); // % configurable por negocio
  const [abonoInicial, setAbonoInicial] = useState(''); // abono para apartar (solo crédito)
  const [modoAbono, setModoAbono] = useState('L'); // 'L' monto o '%' del total
  const [nuevoCli, setNuevoCli] = useState(false); // form de nuevo cliente
  const [cliNombre, setCliNombre] = useState('');
  const [cliTel, setCliTel] = useState('');
  const [cliEmail, setCliEmail] = useState('');
  const [cliNotas, setCliNotas] = useState('');
  const [cliEsLocal, setCliEsLocal] = useState(true);
  const [cliCiudad, setCliCiudad] = useState('');
  const [guardandoCli, setGuardandoCli] = useState(false);
  const [pedidoActivo, setPedidoActivo] = useState(pedido);
  const [apartados, setApartados] = useState({}); // piezas apartadas por pedidos del catálogo
  const [campana, setCampana] = useState(null); // campaña de recompra vigente: { c, clientes:Set }

  // Venta desde un pedido: carga las prendas, canal, anuncio y envío.
  useEffect(() => {
    if (!pedido) return;
    const { items, faltan } = itemsDePedido(pedido, productos);
    setCarrito(items.map(({ producto: p, cantidad }) => ({
      producto_id: p.id, codigo: p.codigo, nombre: p.nombre,
      precio: precioEfectivo(p), stock: p.stock || 0, unidad: unidadDe(p),
      foto: obtenerFotoPrincipal(p), cantidad, cantTexto: String(cantidad),
    })));
    setCanal(canalDeOrigen(pedido.origen));
    setPorAnuncio(vinoPorAnuncio(pedido.origen));
    if (Number(pedido.envio) > 0) setEnvio(String(Number(pedido.envio)));
    setNota(`Pedido del catálogo${pedido.nota ? ` · ${pedido.nota}` : ''}`.slice(0, 200));
    if (faltan.length) mostrarToast(`No se pudo cargar: ${faltan.join(', ')}. Revisa el carrito.`, 'error', 7000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    obtenerClientes().then((cs) => {
      setClientes(cs);
      if (!pedido) return;
      // La clienta del pedido: si ya está en Clientes se elige; si no, se abre el
      // formulario de cliente nuevo con su nombre y teléfono para guardarla con un toque.
      const ya = clienteDePedido(pedido, cs);
      if (ya) setClienteId(ya.id);
      else {
        setNuevoCli(true);
        setCliNombre(pedido.cliente_nombre || '');
        setCliTel(pedido.cliente_telefono || '');
      }
    }).catch(() => {});
    obtenerCuentas().then((cs) => {
      setCuentas(cs);
      if (cs.length) setCuentaId(cs[0].id);
    }).catch(() => {});
    obtenerComisionPct().then(setComisionPct).catch(() => {});
    obtenerApartados().then(setApartados).catch(() => {});
    // Campaña de recompra vigente: a sus clientas se les ofrece su % al venderles.
    obtenerCampanaActiva().then(async (c) => {
      if (!c || c.desde > fechaLocal()) return;
      const env = await obtenerEnvios(c.id);
      setCampana({ c, clientes: new Set(env.map((e) => e.cliente_id)) });
    }).catch(() => {});
  // Solo al abrir: el pedido no cambia (POSView se monta de nuevo con otro pedido).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const texto = busqueda.trim().toLowerCase();
  const hayConStock = useMemo(() => productos.some((p) => (p.stock || 0) > 0), [productos]);
  // Categorías con productos en stock, para las pestañas de filtro.
  const categoriasDisponibles = useMemo(() => {
    const set = new Set(productos.filter((p) => (p.stock || 0) > 0).map((p) => p.categoria).filter(Boolean));
    return [...set].sort();
  }, [productos]);
  // Muestra los productos con stock (filtrados por categoría y búsqueda).
  // Se ven de una para tocarlos, no hay que adivinar que se debe escribir.
  const productosVisibles = useMemo(() => {
    return productos
      .filter((p) => (p.stock || 0) > 0)
      .filter((p) => !categoriaFiltro || p.categoria === categoriaFiltro)
      .filter((p) =>
        !texto ||
        (p.nombre || '').toLowerCase().includes(texto) ||
        (p.marca || '').toLowerCase().includes(texto) ||
        (p.codigo || '').toLowerCase().includes(texto)
      );
  }, [texto, categoriaFiltro, productos]);

  const cantEnCarrito = (id) => carrito.find((i) => i.producto_id === id)?.cantidad || 0;

  // Pieza apartada para un pedido del catálogo (sql/123): se avisa antes de venderla
  // a otra persona. Si esta venta ES de ese pedido, no se pregunta.
  const apartadaParaOtra = (p) => {
    const a = apartados[p.id];
    if (!a) return null;
    const delMismoPedido = pedidoActivo && a.grupos.includes(pedidoActivo.grupo);
    if (delMismoPedido) return null;
    const libres = Number(p.stock || 0) - a.cantidad;
    return cantEnCarrito(p.id) + 1 > libres ? a : null;
  };

  const agregar = (p) => {
    const a = apartadaParaOtra(p);
    if (a && !window.confirm(`⏳ "${p.nombre}" está apartada para el pedido de ${[...new Set(a.clientes)].join(', ')}.

¿Venderla de todas formas? Ese pedido se quedaría sin la pieza.`)) return;
    setCarrito((prev) => {
      const existe = prev.find((i) => i.producto_id === p.id);
      if (existe) {
        // Por peso, tocar de nuevo no suma: las libras se escriben en el carrito.
        if (esPorPeso(existe.unidad)) return prev;
        if (existe.cantidad >= (p.stock || 0)) {
          mostrarToast(`Solo hay ${p.stock} de ${p.nombre}`, 'error');
          return prev;
        }
        return prev.map((i) => i.producto_id === p.id ? { ...i, cantidad: i.cantidad + 1 } : i);
      }
      const unidad = unidadDe(p);
      // Por peso arranca en 1 lb (o lo que quede si es menos) y se corrige escribiendo.
      const inicial = esPorPeso(unidad) ? Math.min(1, redondearCantidad(p.stock || 0)) : 1;
      return [...prev, {
        producto_id: p.id, codigo: p.codigo, nombre: p.nombre,
        precio: precioEfectivo(p), stock: p.stock || 0, unidad,
        foto: obtenerFotoPrincipal(p), cantidad: inicial, cantTexto: String(inicial),
      }];
    });
    setBusqueda('');
  };

  // Producto creado al vuelo (rápido): se agrega directo al carrito con su
  // cantidad y se refresca la lista para que aparezca en el grid/inventario.
  const agregarProductoNuevo = (prod, cant) => {
    setCarrito((prev) => [...prev, {
      producto_id: prod.id, codigo: prod.codigo, nombre: prod.nombre,
      precio: precioEfectivo(prod), stock: prod.stock || 0, unidad: unidadDe(prod),
      foto: obtenerFotoPrincipal(prod), cantidad: Math.max(cant, 1), cantTexto: String(Math.max(cant, 1)),
    }]);
    if (onProductoCreado) onProductoCreado();
  };

  // La lectora escribe el código y da Enter → agrega el producto exacto.
  // Normalizamos (solo letras/números) para tolerar que el lector mande el
  // guion como apóstrofo/espacio según su configuración de teclado.
  const normCodigo = (s) => (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const manejarEnter = (e) => {
    if (e.key !== 'Enter') return;
    const codigo = normCodigo(e.target.value); // valor real del campo
    if (!codigo) return;
    const exacto = productos.find((p) => normCodigo(p.codigo) === codigo);
    if (exacto && (exacto.stock || 0) > 0) agregar(exacto);
    else if (productosVisibles.length === 1) agregar(productosVisibles[0]);
  };

  const cambiarCantidad = (id, delta) => {
    setCarrito((prev) => prev.flatMap((i) => {
      if (i.producto_id !== id) return [i];
      const nueva = redondearCantidad(i.cantidad + delta);
      if (nueva <= 0 || (!esPorPeso(i.unidad) && nueva < 1)) return [];
      if (nueva > i.stock) {
        mostrarToast(`Solo hay ${formatoCantidad(i.stock, i.unidad)} en stock`, 'error');
        return [i];
      }
      return [{ ...i, cantidad: nueva, cantTexto: String(nueva) }];
    }));
  };

  // Productos por peso: las libras se escriben (12.5). Mientras se escribe
  // se guarda el texto; la cantidad numérica es la que se cobra.
  const escribirCantidad = (id, texto) => {
    setCarrito((prev) => prev.map((i) => i.producto_id === id
      ? { ...i, cantTexto: texto, cantidad: redondearCantidad(numeroSeguro(texto)) }
      : i));
  };

  const quitar = (id) => setCarrito((prev) => prev.filter((i) => i.producto_id !== id));

  // Descuento % individual de un renglón del carrito.
  const setDescPct = (id, val) => setCarrito((prev) => prev.map((i) => i.producto_id === id ? { ...i, descPct: val } : i));

  // Descuentos en dos niveles: por producto (% en cada renglón) y global.
  const round2 = (n) => Math.round(n * 100) / 100;
  const lineBase = (i) => i.precio * i.cantidad;
  const lineDescL = (i) => {
    const pct = numeroSeguro(i.descPct);
    return Math.min(Math.max(round2((lineBase(i) * pct) / 100), 0), lineBase(i));
  };
  const subtotal = carrito.reduce((s, i) => s + lineBase(i), 0);
  const descLineas = round2(carrito.reduce((s, i) => s + lineDescL(i), 0));
  const subtotalNeto = subtotal - descLineas;
  // El descuento global se puede escribir en lempiras o como % del neto.
  const descNum = numeroSeguro(descuento);
  const descGlobalBruto = modoDesc === '%' ? (subtotalNeto * descNum) / 100 : descNum;
  const descGlobal = Math.min(Math.max(round2(descGlobalBruto), 0), subtotalNeto);
  const totalProductos = subtotalNeto - descGlobal;
  // Envío: en ventas pagadas entra a la cuenta; en crédito se suma a lo que
  // la clienta debe (Por cobrar). En ambos casos suma al total de la venta.
  const envioNum = Math.max(numeroSeguro(envio), 0);
  const total = totalProductos + envioNum;
  // Abono inicial (solo crédito): lo que la clienta deja para apartar.
  const abonoNum = numeroSeguro(abonoInicial);
  const abonoBruto = modoAbono === '%' ? (total * abonoNum) / 100 : abonoNum;
  const abonoInicialL = aCredito ? Math.min(Math.max(round2(abonoBruto), 0), total) : 0;
  const saldoPendiente = total - abonoInicialL;

  // Vuelto: solo tiene sentido si la venta entra a una cuenta de efectivo.
  const cuentaSel = cuentas.find((c) => c.id === cuentaId);
  const esEfectivo = cuentaSel?.tipo === 'efectivo';
  const pagaConNum = numeroSeguro(pagaCon);
  const vuelto = pagaConNum - total;

  // Comisión bancaria (POS / Link de pago): el banco rebaja el 4% de lo que
  // se cobra AHORA. En venta pagada, la base es el total; en crédito, el abono
  // inicial (lo único que entra a la cuenta en ese momento).
  const baseComision = aCredito ? abonoInicialL : total;
  const comisionMonto = conComisionPOS ? calcularComision(baseComision, comisionPct) : 0;

  // Crea un cliente al vuelo desde el POS y lo deja seleccionado.
  const guardarClienteRapido = async () => {
    if (!cliNombre.trim()) { mostrarToast('Escribe el nombre del cliente', 'error'); return; }
    setGuardandoCli(true);
    try {
      const nuevo = await crearCliente({ nombre: cliNombre, telefono: cliTel, email: cliEmail, notas: cliNotas, esLocal: cliEsLocal, ciudad: cliCiudad });
      setClientes((prev) => [...prev, nuevo].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')));
      setClienteId(nuevo.id);
      setNuevoCli(false); setCliNombre(''); setCliTel(''); setCliEmail(''); setCliNotas(''); setCliEsLocal(true); setCliCiudad('');
      mostrarToast('Cliente creado ✅', 'exito');
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardandoCli(false);
    }
  };

  const cobrar = async () => {
    if (carrito.length === 0) {
      mostrarToast('Agrega productos al carrito primero', 'error');
      return;
    }
    if (aCredito && !clienteId) {
      mostrarToast('Para vender a crédito, elige o crea un cliente (para saber quién debe)', 'error', 5000);
      return;
    }
    if (aCredito && abonoInicialL > 0 && !cuentaId) {
      mostrarToast('Elige la cuenta donde entra el abono', 'error');
      return;
    }
    const sinPeso = carrito.find((i) => esPorPeso(i.unidad) && !(i.cantidad > 0));
    if (sinPeso) {
      mostrarToast(`Escribe cuántas libras de ${sinPeso.nombre} vas a vender`, 'error');
      return;
    }
    setCobrando(true);
    try {
      const itemsVenta = carrito.map((i) => ({ producto_id: i.producto_id, cantidad: i.cantidad, unidad: i.unidad, descuento: lineDescL(i) }));
      const ticket = nuevoTicket();
      const datosVenta = {
        clienteId: clienteId || null,
        metodoPago: aCredito ? null : (cuentas.find((c) => c.id === cuentaId)?.nombre || null),
        cuentaId: (!aCredito || abonoInicialL > 0) ? (cuentaId || null) : null,
        descuento: descGlobal,
        concepto: concepto || null,
        nota: nota || null,
        canal: canal || null,
        porAnuncio,
        aCredito,
        fecha: timestampParaFecha(fechaVenta),
        envio: envioNum,
        abonoInicial: abonoInicialL,
      };
      const comision = comisionMonto > 0 && cuentaId ? {
        monto: comisionMonto,
        cuentaId,
        fecha: fechaVenta,
        detalle: `POS / Link${concepto ? ' · ' + concepto : ''}`,
      } : null;
      // Sin internet: la venta se guarda en este dispositivo (con su fecha y
      // hora reales) y se sube sola cuando vuelve la señal.
      let sinInternet = false;
      try {
        await registrarVentaCarrito(itemsVenta, { ...datosVenta, ticket });
      } catch (err) {
        if (!esErrorDeRed(err)) throw err;
        sinInternet = true;
        guardarVentaPendiente(negocio?.id, {
          ticket,
          userId: sesion?.user?.id || null,
          args: argsVentaCarrito(itemsVenta, { ...datosVenta, fecha: datosVenta.fecha || new Date().toISOString() }),
          comision,
          resumen: {
            total,
            productos: carrito.map((i) => `${formatoCantidad(i.cantidad, i.unidad)} × ${i.nombre}`),
            cliente: clientes.find((c) => c.id === clienteId)?.nombre || null,
          },
        });
      }
      // Comisión bancaria automática: se rebaja de la misma cuenta como gasto.
      // Si falla, la venta ya quedó guardada → avisamos para agregarla a mano.
      // (Sin internet se registra junto con la venta al subirla.)
      if (comision && !sinInternet) {
        try {
          await registrarComisionBancaria(comision);
        } catch (err) {
          mostrarToast('La venta se registró, pero no se pudo registrar la comisión bancaria. Agrégala a mano en 💸 Gastos.', 'error', 7000);
        }
      }
      const cant = carrito.reduce((s, i) => s + (esPorPeso(i.unidad) ? 1 : i.cantidad), 0);
      if (sinInternet) mostrarToast('📴 Sin internet: la venta quedó guardada en este dispositivo y se sube sola cuando vuelva la señal.', 'exito', 6000);
      else mostrarToast(aCredito
        ? (abonoInicialL > 0
            ? `Apartado registrado · abonó ${fmt(abonoInicialL)}, debe ${fmt(saldoPendiente)} 🧾`
            : `Venta a crédito registrada · ${fmt(total)}. Queda en Por cobrar 🧾`)
        : `¡Venta cobrada! ${cant} producto(s) · ${fmt(total)} 💰`, 'exito');
      // Para generar el comprobante de esta venta.
      setUltimaVenta({ total, ticket, sinInternet });
      setCarrito([]);
      setDescuento('');
      setEnvio('');
      setPagaCon('');
      setConcepto('');
      setNota('');
      setCanal(CANAL_DEFECTO);
      setPorAnuncio(false);
      setFechaVenta(fechaLocal());
      setAcredito(false);
      setConComisionPOS(false);
      setAbonoInicial('');
      setModoAbono('%');
      // Venta de un pedido del catálogo: el pedido queda "Compró" y pasa al Historial.
      if (pedidoActivo) {
        const ped = pedidoActivo;
        setPedidoActivo(null);
        try {
          await registrarResultadoPedido(ped.ids || ped.id, {
            resultado: 'compro', nota: ped.nota_resultado || null,
            atendidoEn: ped.estado === 'pendiente' ? new Date().toISOString() : null,
          });
          mostrarToast(`✅ Venta cobrada y el pedido de ${ped.cliente_nombre} quedó como "Compró"`, 'exito', 5000);
        } catch (err) {
          mostrarToast(`La venta se registró, pero no se pudo marcar el pedido de ${ped.cliente_nombre}. Márcalo como "Compró" en Pedidos.`, 'error', 7000);
        }
        onPedidoVendido && onPedidoVendido();
      }
      onVendido();
    } catch (e) {
      const msg = (e?.message || '').includes('STOCK_INSUFICIENTE')
        ? 'No hay stock suficiente para uno de los productos.'
        : mensajeAmigable(e);
      mostrarToast(msg, 'error');
    } finally {
      setCobrando(false);
    }
  };

  const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };

  // Casilla "cobrado con POS/Link Ficohsa (−4%)". Sirve igual para venta
  // pagada (base = total) y para el abono de un apartado (base = abono).
  const casillaComision = (
    <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', padding: '10px 12px', margin: '0 0 10px', cursor: cobrando ? 'default' : 'pointer' }}>
      <input type="checkbox" checked={conComisionPOS} disabled={cobrando}
        onChange={(e) => setConComisionPOS(e.target.checked)} style={{ marginTop: '2px', width: '16px', height: '16px', flexShrink: 0 }} />
      <span style={{ fontSize: '12px', color: '#1E40AF' }}>
        <strong>💳 Cobrado con POS o Link de pago Ficohsa</strong> <span style={{ color: '#1D4ED8' }}>(−{comisionPct}% comisión)</span>
        {conComisionPOS && comisionMonto > 0 ? (
          <span style={{ display: 'block', marginTop: '3px', color: '#1D4ED8', fontWeight: 700 }}>
            Se rebajará {fmt(comisionMonto)} de tu cuenta como comisión bancaria.
          </span>
        ) : (
          <span style={{ display: 'block', marginTop: '3px', color: '#3B82F6' }}>
            El banco te debita el {comisionPct}%. Se registra solo como gasto "Comisiones bancarias".
          </span>
        )}
      </span>
    </label>
  );

  return (
    <div className="k-page">
      <div className="k-page-head">
        <h2 className="k-page-title">🛒 Vender</h2>
        <div className="k-actions" style={{ alignItems: 'center' }}>
          <button onClick={() => setMostrarIngresoLibre(true)}
            title="Vender algo que no está en el inventario, o registrar otro ingreso"
            style={{ backgroundColor: '#ECFDF5', border: '1px solid #6EE7B7', borderRadius: '9px', padding: '7px 14px', cursor: 'pointer', color: 'var(--k-verde)', fontWeight: 'bold', fontSize: '13px' }}>
            💵 Venta libre
          </button>
          <button onClick={onVolver} className="k-btn-soft">← Volver</button>
        </div>
      </div>

      {pedidoActivo && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', background: '#FDF2F8', border: '1px solid #F9A8D4', borderRadius: '12px', padding: '12px 14px', marginBottom: '14px' }}>
          <span style={{ fontSize: '14px', color: '#831843' }}>
            🛒 <b>Venta del pedido de {pedidoActivo.cliente_nombre}</b> · llegó por {etiquetaOrigen(pedidoActivo.origen).icono} {etiquetaOrigen(pedidoActivo.origen).etiqueta}
            {vinoPorAnuncio(pedidoActivo.origen) && ' (📣 ya va marcado "vino por anuncio")'}.
            <br /><small style={{ color: '#9D174D' }}>Revisa la cuenta donde entra el dinero y cobra. Al cobrar, el pedido queda como "Compró".</small>
          </span>
          <button onClick={() => setPedidoActivo(null)} title="Vender normal, sin ligar a este pedido" className="k-btn-soft">✕ No ligar al pedido</button>
        </div>
      )}

      {campana && clienteId && campana.clientes.has(clienteId) && Number(campana.c.descuento_pct) > 0 && carrito.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '12px', padding: '10px 14px', marginBottom: '14px' }}>
          <span style={{ fontSize: '14px', color: '#92400E' }}>
            🎁 <b>{clientes.find((c) => c.id === clienteId)?.nombre}</b> es de tu {campana.c.nombre}: tiene <b>{Number(campana.c.descuento_pct)}%</b> de descuento de clienta.
          </span>
          {modoDesc === '%' && Number(descuento) === Number(campana.c.descuento_pct)
            ? <span style={{ fontSize: '13px', fontWeight: 700, color: '#047857' }}>✅ Aplicado</span>
            : <button onClick={() => { setModoDesc('%'); setDescuento(String(Number(campana.c.descuento_pct))); }} disabled={cobrando}
                style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: '#D97706', color: 'white', fontWeight: 700, cursor: 'pointer' }}>
                Aplicar {Number(campana.c.descuento_pct)}%
              </button>}
        </div>
      )}

      {ultimaVenta && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', background: '#ECFDF5', border: '1px solid #6EE7B7', borderRadius: '12px', padding: '12px 14px', marginBottom: '14px' }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#065F46' }}>✅ Venta cobrada · {fmt(ultimaVenta.total)}</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            {ultimaVenta.sinInternet ? (
              <span style={{ fontSize: '12px', color: '#065F46' }}>🧾 El comprobante se puede sacar en Ventas de hoy cuando la venta se suba.</span>
            ) : (
              <button onClick={() => setVerComprobante(true)}
                style={{ padding: '9px 16px', background: 'var(--color-primario,#DB2777)', color: '#fff', border: 'none', borderRadius: '9px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>🧾 Comprobante</button>
            )}
            <button onClick={() => setUltimaVenta(null)}
              style={{ padding: '9px 12px', background: 'none', border: '1px solid #A7F3D0', borderRadius: '9px', color: '#065F46', cursor: 'pointer', fontSize: '13px' }}>✕</button>
          </div>
        </div>
      )}
      {verComprobante && ultimaVenta?.ticket && <ComprobanteModal ticket={ultimaVenta.ticket} onCerrar={() => setVerComprobante(false)} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', alignItems: 'start' }}>
        {/* Productos: se ven de una, se tocan para agregar al carrito */}
        <div>
          <input type="search" value={busqueda} autoFocus
            placeholder="🔍 Filtrar por nombre, marca o código (o escanear)"
            onChange={(e) => setBusqueda(e.target.value)} onKeyDown={manejarEnter}
            style={{ ...input, backgroundColor: 'white', marginBottom: '6px' }} />
          <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#9CA3AF' }}>
            👇 Toca un producto para agregarlo al carrito. Con lectora, escanea y se agrega solo.
          </p>

          <button type="button" onClick={() => setMostrarNuevoProd(true)}
            title="Crear y vender un producto que no está en el inventario"
            style={{ width: '100%', marginBottom: '10px', padding: '9px', backgroundColor: 'white', border: '1px dashed var(--color-primario,#DB2777)', borderRadius: '8px', cursor: 'pointer', color: 'var(--color-primario,#DB2777)', fontWeight: 'bold', fontSize: '13px' }}>
            ➕ ¿No está en la lista? Crear producto nuevo
          </button>

          {categoriasDisponibles.length > 1 && (
            <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '10px', marginBottom: '4px' }}>
              {['', ...categoriasDisponibles].map((cat) => {
                const activo = categoriaFiltro === cat;
                return (
                  <button key={cat || 'todas'} type="button" onClick={() => setCategoriaFiltro(cat)}
                    style={{ flexShrink: 0, padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                      border: `1px solid ${activo ? 'var(--color-primario,#DB2777)' : '#E5E7EB'}`,
                      backgroundColor: activo ? 'var(--color-primario,#DB2777)' : 'white',
                      color: activo ? 'white' : '#6B7280' }}>
                    {cat || 'Todas'}
                  </button>
                );
              })}
            </div>
          )}

          {productosVisibles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 16px', backgroundColor: 'white', borderRadius: '12px', border: '1px dashed #E5E7EB', color: '#9CA3AF', fontSize: '14px' }}>
              {hayConStock
                ? 'No se encontró ningún producto con esa búsqueda.'
                : 'No tienes productos con stock disponible. Agrega productos en 📦 Inventario, o usa 💵 Venta libre.'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px', maxHeight: '540px', overflowY: 'auto', paddingRight: '2px' }}>
              {productosVisibles.map((p) => {
                const enCarrito = cantEnCarrito(p.id);
                const agotadoEnCarrito = !esPorPeso(p) && enCarrito >= (p.stock || 0);
                return (
                  <button key={p.id} type="button" onClick={() => agregar(p)}
                    style={{ position: 'relative', textAlign: 'left', backgroundColor: 'white', border: enCarrito ? '2px solid var(--k-verde)' : '1px solid #E5E7EB', borderRadius: '12px', padding: '8px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '6px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    {enCarrito > 0 && (
                      <span style={{ position: 'absolute', top: '6px', right: '6px', backgroundColor: 'var(--k-verde)', color: 'white', fontSize: '11px', fontWeight: 'bold', borderRadius: '999px', minWidth: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>
                        {esPorPeso(p) ? '✓' : enCarrito}
                      </span>
                    )}
                    <div style={{ width: '100%', height: '90px', borderRadius: '8px', backgroundColor: '#F9FAFB', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '30px' }}>
                      {obtenerFotoPrincipal(p) ? <img src={optimizarFoto(obtenerFotoPrincipal(p), 300)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '📦'}
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '10px', color: '#9CA3AF', fontWeight: 600, letterSpacing: '0.3px' }}>{p.codigo}</p>
                      <p style={{ margin: '1px 0 0', fontSize: '13px', fontWeight: 600, color: '#111827', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', minHeight: '32px' }}>{p.nombre}</p>
                      {p.marca && <p style={{ margin: '1px 0 0', fontSize: '11px', color: '#6B7280', fontWeight: 600 }}>👜 {p.marca}</p>}
                      {enOferta(p) ? (
                        <p style={{ margin: '2px 0 0' }}>
                          <span style={{ fontSize: '11px', color: '#9CA3AF', textDecoration: 'line-through', marginRight: '4px' }}>{fmt(p.precio_venta)}</span>
                          <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#EA580C' }}>{fmt(precioEfectivo(p))}</span>
                          <span style={{ display: 'inline-block', marginLeft: '4px', fontSize: '9px', fontWeight: 700, color: 'white', backgroundColor: '#EA580C', padding: '1px 4px', borderRadius: '4px' }}>-{pctOferta(p)}%</span>
                        </p>
                      ) : (
                        <p style={{ margin: '2px 0 0', fontSize: '15px', fontWeight: 'bold', color: 'var(--color-primario,#DB2777)' }}>{fmt(p.precio_venta)}{esPorPeso(p) && <span style={{ fontSize: '11px', fontWeight: 600 }}> /{unidadDe(p)}</span>}</p>
                      )}
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: agotadoEnCarrito ? '#DC2626' : '#9CA3AF' }}>
                        {agotadoEnCarrito ? 'Sin más stock' : `Stock: ${formatoCantidad(p.stock, p)}`}{p.talla ? ` · T${p.talla}` : ''}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Carrito */}
        <div style={{ backgroundColor: 'white', borderRadius: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #F3F4F6', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', backgroundColor: 'color-mix(in srgb, var(--color-primario,#DB2777) 7%, white)' }}>
            <p style={{ margin: 0, fontWeight: 'bold', color: 'var(--color-primario,#DB2777)' }}>🛒 Carrito ({carrito.reduce((s, i) => s + (esPorPeso(i.unidad) ? 1 : i.cantidad), 0)})</p>
          </div>

          {carrito.length === 0 ? (
            <p style={{ margin: 0, padding: '32px 16px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}>
              Toca un producto para agregarlo aquí 🛒
            </p>
          ) : (
            <div>
              {carrito.map((i) => {
                const dL = lineDescL(i);
                return (
                <div key={i.producto_id} style={{ padding: '10px 16px', borderBottom: '1px solid #F3F4F6' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Miniatura: para confirmar de un vistazo qué se está vendiendo */}
                    <div style={{ width: '44px', height: '44px', flexShrink: 0, borderRadius: '8px', overflow: 'hidden', backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                      {i.foto
                        ? <img src={optimizarFoto(i.foto, 120)} alt={i.nombre} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : '📦'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#111827' }}>{i.nombre}</p>
                      <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>{i.codigo ? `${i.codigo} · ` : ''}{fmt(i.precio)} {esPorPeso(i.unidad) ? `por ${i.unidad === 'kg' ? 'kilo' : 'libra'}` : 'c/u'}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button onClick={() => cambiarCantidad(i.producto_id, -1)} style={{ width: '26px', height: '26px', borderRadius: '6px', border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer', fontWeight: 'bold' }}>−</button>
                      {esPorPeso(i.unidad) ? (
                        <CampoNumero valor={i.cantTexto ?? String(i.cantidad)} onChange={(v) => escribirCantidad(i.producto_id, v)}
                          decimales={2} min={0.01} max={i.stock} sufijo={i.unidad}
                          maxMensaje={`Solo hay ${formatoCantidad(i.stock, i.unidad)}`}
                          deshabilitado={cobrando} aria-label={`Libras de ${i.nombre}`}
                          estilo={{ width: '70px', padding: '4px 6px', borderRadius: '6px', fontSize: '14px', textAlign: 'center', fontWeight: 600 }} />
                      ) : (
                        <span style={{ minWidth: '20px', textAlign: 'center', fontWeight: 600 }}>{i.cantidad}</span>
                      )}
                      <button onClick={() => cambiarCantidad(i.producto_id, 1)} style={{ width: '26px', height: '26px', borderRadius: '6px', border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer', fontWeight: 'bold' }}>+</button>
                    </div>
                    <button onClick={() => quitar(i.producto_id)} title="Quitar" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontSize: '16px' }}>✕</button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>🏷️ Desc</span>
                    <CampoNumero valor={i.descPct || ''} onChange={(v) => setDescPct(i.producto_id, v)}
                      decimales={0} min={0} max={100} maxMensaje="El descuento no puede pasar del 100%"
                      deshabilitado={cobrando} placeholder="0"
                      estilo={{ width: '52px', padding: '4px 6px', borderRadius: '6px', fontSize: '13px', textAlign: 'center' }} />
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>%</span>
                    <span style={{ marginLeft: 'auto', textAlign: 'right' }}>
                      {dL > 0 && (
                        <span style={{ fontSize: '11px', color: '#9CA3AF', textDecoration: 'line-through', marginRight: '6px' }}>{fmt(lineBase(i))}</span>
                      )}
                      <span style={{ fontWeight: 'bold', color: 'var(--color-primario,#DB2777)', fontSize: '13px' }}>{fmt(lineBase(i) - dL)}</span>
                    </span>
                  </div>
                </div>
                );
              })}

              <div style={{ padding: '14px 16px' }}>
                {/* Tipo de pago: pagada ahora o a crédito (fiado) */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <button type="button" disabled={cobrando} onClick={() => setAcredito(false)}
                    style={{ flex: 1, padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                      border: `1px solid ${!aCredito ? 'var(--k-verde)' : '#E5E7EB'}`, backgroundColor: !aCredito ? 'var(--k-verde)' : 'white', color: !aCredito ? 'white' : '#6B7280' }}>
                    💵 Pagada
                  </button>
                  <button type="button" disabled={cobrando} onClick={() => setAcredito(true)}
                    style={{ flex: 1, padding: '8px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                      border: `1px solid ${aCredito ? '#B45309' : '#E5E7EB'}`, backgroundColor: aCredito ? '#F59E0B' : 'white', color: aCredito ? 'white' : '#6B7280' }}>
                    🧾 A crédito (fiado)
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: aCredito ? '1fr' : '1fr 1fr', gap: '8px', marginBottom: nuevoCli ? '0' : '10px' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Cliente {aCredito ? '*' : '(opcional)'}</label>
                      <button type="button" disabled={cobrando} onClick={() => setNuevoCli((v) => !v)}
                        style={{ background: 'none', border: 'none', color: 'var(--color-primario,#DB2777)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                        {nuevoCli ? 'Cancelar' : '➕ Nuevo'}
                      </button>
                    </div>
                    <select value={clienteId} disabled={cobrando} onChange={(e) => setClienteId(e.target.value)} style={input}>
                      <option value="">Sin cliente</option>
                      {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                  </div>
                  {!aCredito && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Forma de pago / cuenta</label>
                      <select value={cuentaId} disabled={cobrando} onChange={(e) => setCuentaId(e.target.value)} style={input}>
                        {cuentas.length === 0 && <option value="">Crea una cuenta primero</option>}
                        {cuentas.map((c) => <option key={c.id} value={c.id}>{iconoTipo(c.tipo)} {c.nombre}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                {aCredito && (
                  <div style={{ margin: '0 0 10px', backgroundColor: '#FEF3C7', borderRadius: '8px', padding: '10px 12px', border: '1px solid #FDE68A' }}>
                    <label style={{ fontSize: '12px', color: '#92400E', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                      🧾 Abono inicial para apartar (opcional)
                    </label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <div style={{ flex: 1 }}>
                        <CampoNumero valor={abonoInicial} onChange={setAbonoInicial} deshabilitado={cobrando}
                          min={0} max={modoAbono === '%' ? 100 : total}
                          maxMensaje={modoAbono === '%' ? 'El abono no puede pasar del 100%' : `El abono no puede ser mayor al total (${fmt(total)})`}
                          placeholder={modoAbono === '%' ? 'Ej: 50' : '0.00'}
                          sufijo={modoAbono === '%' ? '%' : undefined} prefijo={modoAbono === '%' ? undefined : 'L'}
                          estilo={input} />
                      </div>
                      <div style={{ display: 'flex', border: '1px solid #FDE68A', borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }}>
                        {['L', '%'].map((m) => (
                          <button key={m} type="button" disabled={cobrando} onClick={() => { setModoAbono(m); setAbonoInicial(''); }}
                            style={{ padding: '0 14px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '13px',
                              backgroundColor: modoAbono === m ? '#B45309' : 'white', color: modoAbono === m ? 'white' : '#92400E' }}>
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>
                    {abonoInicialL > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <label style={{ fontSize: '12px', color: '#92400E', fontWeight: 600, display: 'block', marginBottom: '2px' }}>¿A qué cuenta entra el abono?</label>
                        <select value={cuentaId} disabled={cobrando} onChange={(e) => setCuentaId(e.target.value)} style={input}>
                          {cuentas.length === 0 && <option value="">Crea una cuenta primero</option>}
                          {cuentas.map((c) => <option key={c.id} value={c.id}>{iconoTipo(c.tipo)} {c.nombre}</option>)}
                        </select>
                      </div>
                    )}
                    {abonoInicialL > 0 && cuentaId && (
                      <div style={{ marginTop: '8px' }}>{casillaComision}</div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '8px', borderTop: '1px dashed #FDE68A', fontSize: '14px', fontWeight: 700, color: '#92400E' }}>
                      <span>Abona ahora: {fmt(abonoInicialL)}</span>
                      <span>Queda debiendo: {fmt(saldoPendiente)}</span>
                    </div>
                    <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#B45309' }}>
                      El resto queda <strong>pendiente en Por cobrar</strong>. Registra los siguientes pagos cuando te paguen.
                    </p>
                  </div>
                )}

                {nuevoCli && (
                  <div style={{ backgroundColor: '#FFF5F7', borderRadius: '8px', padding: '10px', border: '1px solid #FBCFE8', margin: '8px 0 10px' }}>
                    <input type="text" value={cliNombre} disabled={guardandoCli} autoFocus
                      onChange={(e) => setCliNombre(e.target.value)} placeholder="Nombre del cliente *"
                      style={{ ...input, marginBottom: '6px' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '8px' }}>
                      <input type="tel" value={cliTel} disabled={guardandoCli}
                        onChange={(e) => setCliTel(e.target.value)} placeholder="Teléfono (opcional)" style={input} />
                      <input type="email" value={cliEmail} disabled={guardandoCli}
                        onChange={(e) => setCliEmail(e.target.value)} placeholder="Correo (opcional)" style={input} />
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <UbicacionCliente esLocal={cliEsLocal} ciudad={cliCiudad} disabled={guardandoCli}
                        onEsLocal={setCliEsLocal} onCiudad={setCliCiudad} />
                    </div>
                    <input type="text" value={cliNotas} disabled={guardandoCli}
                      onChange={(e) => setCliNotas(e.target.value)} placeholder="Notas / comentario (ej. gustos, tallas)"
                      style={{ ...input, marginBottom: '8px' }} />
                    <button type="button" onClick={guardarClienteRapido} disabled={guardandoCli}
                      style={{ width: '100%', padding: '9px', backgroundColor: guardandoCli ? '#9CA3AF' : 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: guardandoCli ? 'wait' : 'pointer' }}>
                      {guardandoCli ? '⏳ Guardando...' : 'Guardar y usar'}
                    </button>
                  </div>
                )}
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>🗓️ Fecha de la venta</label>
                  <input type="date" value={fechaVenta} max={fechaLocal()} disabled={cobrando}
                    onChange={(e) => setFechaVenta(e.target.value)} style={input} />
                  <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
                    {fechaVenta === fechaLocal()
                      ? `Hoy · ${horaLocal()} (hora actual)`
                      : '📅 Venta de otro día · se guarda con la hora actual'}
                  </p>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Canal de venta</label>
                  <select value={canal} disabled={cobrando} onChange={(e) => setCanal(e.target.value)} style={input}>
                    {CANALES.map((c) => <option key={c.valor} value={c.valor}>{c.icono} {c.valor}</option>)}
                  </select>
                  {/* Para medir cuánto venden los anuncios de verdad (módulo Anuncios de Meta). */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', padding: '8px 10px', borderRadius: '10px', cursor: 'pointer',
                    border: `1px solid ${porAnuncio ? 'var(--color-primario,#DB2777)' : '#E5E7EB'}`, backgroundColor: porAnuncio ? '#FDF2F8' : 'white', fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                    <input type="checkbox" checked={porAnuncio} disabled={cobrando} onChange={(e) => setPorAnuncio(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: 'var(--color-primario,#DB2777)' }} />
                    📣 ¿Vino por un anuncio?
                  </label>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Descuento global (extra a todo)</label>
                    <div style={{ display: 'flex', border: '1px solid #D1D5DB', borderRadius: '8px', overflow: 'hidden' }}>
                      {['L', '%'].map((m) => (
                        <button key={m} type="button" disabled={cobrando}
                          onClick={() => { setModoDesc(m); setDescuento(''); }}
                          style={{ padding: '4px 14px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '13px',
                            backgroundColor: modoDesc === m ? 'var(--color-primario,#DB2777)' : 'white',
                            color: modoDesc === m ? 'white' : '#6B7280' }}>
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>

                  {modoDesc === 'L' ? (
                    <CampoNumero valor={descuento} onChange={setDescuento} deshabilitado={cobrando}
                      min={0} max={subtotalNeto} prefijo="L" placeholder="0.00"
                      maxMensaje={`El descuento no puede pasar del subtotal (${fmt(subtotalNeto)})`}
                      estilo={input} />
                  ) : (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                      <div style={{ flex: '0 0 120px' }}>
                        <label style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, display: 'block', marginBottom: '2px' }}>% a descontar</label>
                        <CampoNumero valor={descuento} onChange={setDescuento} deshabilitado={cobrando}
                          min={0} max={100} sufijo="%" placeholder="Ej: 20"
                          maxMensaje="El descuento no puede pasar del 100%"
                          estilo={input} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Descuento (L)</label>
                        <div style={{ ...input, backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', color: 'var(--k-verde)', fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                          {fmt(descGlobal)}
                        </div>
                      </div>
                    </div>
                  )}
                  {modoDesc === '%' && descNum > 0 && (
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
                      {descNum}% de {fmt(subtotalNeto)} (después de descuentos por producto)
                    </p>
                  )}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Concepto (opcional)</label>
                  <input type="text" value={concepto} disabled={cobrando}
                    onChange={(e) => setConcepto(e.target.value)} style={input} placeholder="Ej: Venta a Gisela por WhatsApp" />
                  <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#9CA3AF' }}>Aparece en el Balance. Si lo dejas vacío, dice "Venta (N productos)".</p>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>Nota (opcional)</label>
                  <input type="text" value={nota} disabled={cobrando}
                    onChange={(e) => setNota(e.target.value)} style={input} placeholder="Ej: Entregar el viernes" />
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>🚚 Envío nacional (opcional)</label>
                  <CampoNumero valor={envio} onChange={setEnvio} deshabilitado={cobrando}
                    min={0} prefijo="L" placeholder="0.00" estilo={input} />
                  <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
                    {aCredito
                      ? 'Se suma a lo que te deben (Por cobrar). Cuando pagues al courier, regístralo como 💸 Gasto "Envíos".'
                      : 'Se suma al total y entra a tu cuenta. Cuando le pagues al courier, regístralo como 💸 Gasto "Envíos".'}
                  </p>
                </div>

                {!aCredito && cuentaId && casillaComision}

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6B7280', marginBottom: '2px' }}>
                  <span>Subtotal</span><span>{fmt(subtotal)}</span>
                </div>
                {descLineas > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#DC2626', marginBottom: '2px' }}>
                    <span>Descuento por producto</span><span>−{fmt(descLineas)}</span>
                  </div>
                )}
                {descGlobal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#DC2626', marginBottom: '2px' }}>
                    <span>Descuento global</span><span>−{fmt(descGlobal)}</span>
                  </div>
                )}
                {envioNum > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#0EA5E9', marginBottom: '2px' }}>
                    <span>🚚 Envío nacional</span><span>+{fmt(envioNum)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: 'bold', color: '#111827', margin: '8px 0 12px' }}>
                  <span>{envioNum > 0 ? 'Total a cobrar' : 'Total'}</span><span style={{ color: 'var(--color-primario,#DB2777)' }}>{fmt(total)}</span>
                </div>

                {!aCredito && esEfectivo && total > 0 && (
                  <div style={{ backgroundColor: '#F0FDF4', borderRadius: '8px', padding: '10px 12px', border: '1px solid #BBF7D0', marginBottom: '12px' }}>
                    <label style={{ fontSize: '12px', color: '#065F46', fontWeight: 600, display: 'block', marginBottom: '4px' }}>💵 ¿Con cuánto paga? (para el vuelto, opcional)</label>
                    <CampoNumero valor={pagaCon} onChange={setPagaCon} deshabilitado={cobrando}
                      min={0} prefijo="L" placeholder="0.00" estilo={input} />
                    {pagaConNum > 0 && (
                      <p style={{ margin: '8px 0 0', fontSize: '15px', fontWeight: 'bold', color: vuelto >= 0 ? '#065F46' : '#DC2626' }}>
                        {vuelto >= 0 ? `Vuelto: ${fmt(vuelto)}` : `Falta: ${fmt(-vuelto)}`}
                      </p>
                    )}
                  </div>
                )}

                <button onClick={cobrar} disabled={cobrando || carrito.length === 0 || (!aCredito && cuentas.length === 0)}
                  style={{ width: '100%', padding: '14px', backgroundColor: cobrando ? '#9CA3AF' : (aCredito ? '#F59E0B' : 'var(--k-verde)'), color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: cobrando ? 'wait' : 'pointer' }}>
                  {cobrando ? '⏳ Registrando...' : (aCredito ? `🧾 Registrar a crédito ${fmt(total)}` : `💰 Cobrar ${fmt(total)}`)}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {mostrarIngresoLibre && (
        <IngresoLibreModal
          onCerrar={() => setMostrarIngresoLibre(false)}
          onGuardado={onVendido}
        />
      )}

      {mostrarNuevoProd && (
        <ProductoRapidoModal
          onCerrar={() => setMostrarNuevoProd(false)}
          onCreado={agregarProductoNuevo}
        />
      )}
    </div>
  );
}
